import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box, Button, Flex, FormControl, FormHelperText, FormLabel, Input, InputGroup, InputLeftAddon,
  Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
  SimpleGrid, Stack, Text, useToast,
} from '@chakra-ui/react';
import { ServerSpecRow, useServerSpecActions } from '../hooks/useServerSpecs';
import { fromSpecJson, salesOrderDescription } from '../utils/serverSpecCatalog';
import { money } from '../utils/money';
import apiErrorMessage from '../utils/apiError';

// item 1595 (Chad: "convert one of these to a sales order. We'll hand-type in the pricing that
// we get back from the vendor") — the spec becomes an SO on this project: ONE hardware line,
// qty = node count, the full spec as its description, vendor price → cost, sell price → retail.
// It opens in the SO editor; share it to the portal for acceptance, invoice it as any SO.

const toNumber = (s: string) => {
  const n = Number(s.replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : NaN;
};

const ServerSpecConvertModal: React.FC<{
  projectId: string;
  spec: ServerSpecRow | null;
  onClose: () => void;
}> = ({ projectId, spec, onClose }) => {
  const { convert } = useServerSpecActions(projectId);
  const history = useHistory();
  const toast = useToast();
  const [vendorName, setVendorName] = useState('');
  const [quoteNo, setQuoteNo] = useState('');
  const [cost, setCost] = useState('');
  const [price, setPrice] = useState('');

  useEffect(() => {
    setVendorName('');
    setQuoteNo('');
    setCost('');
    setPrice('');
  }, [spec]);

  const nodes = spec?.nodeCount ?? 0;
  const unitCost = cost.trim() === '' ? 0 : toNumber(cost);
  const unitPrice = toNumber(price);
  const valid = Number.isFinite(unitCost) && unitCost >= 0 && Number.isFinite(unitPrice) && unitPrice > 0;
  const margin = valid && unitPrice > 0 ? ((unitPrice - unitCost) / unitPrice) * 100 : null;

  const onConvert = async () => {
    if (!spec || !valid) return;
    try {
      const doc = fromSpecJson(spec.specJson);
      const { data } = await convert.mutateAsync({
        id: spec.id,
        unitCost,
        unitPrice,
        vendorName: vendorName.trim(),
        vendorQuoteNumber: quoteNo.trim(),
        description: salesOrderDescription(spec.name, spec.nodeCount, doc),
      });
      toast({ status: 'success', title: `Sales Order ${data.number ?? ''} created`, description: 'Opening it — review tax, then share it to the customer portal for acceptance.' });
      onClose();
      if (data.workOrderId) history.push(`/WorkOrder/${data.workOrderId}`);
    } catch (e) {
      toast({
        title: 'Could not create the sales order',
        description: apiErrorMessage(e, 'Try again.'),
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={!!spec} onClose={onClose} size="lg" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Convert to sales order
          <Text fontSize="sm" fontWeight="normal" opacity={0.7} mt={1}>
            {spec ? `${spec.name} · ${nodes} identical nodes` : ''}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <SimpleGrid columns={2} spacing={3}>
              <FormControl>
                <FormLabel fontSize="xs" mb={1} opacity={0.75}>Vendor</FormLabel>
                <Input size="sm" value={vendorName} placeholder="Vendor name" onChange={(e) => setVendorName(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={1} opacity={0.75}>Vendor quote #</FormLabel>
                <Input size="sm" value={quoteNo} placeholder="Quote number" onChange={(e) => setQuoteNo(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={1} opacity={0.75}>Vendor price per node</FormLabel>
                <InputGroup size="sm">
                  <InputLeftAddon>$</InputLeftAddon>
                  <Input inputMode="decimal" value={cost} placeholder="0.00" onChange={(e) => setCost(e.target.value)} />
                </InputGroup>
                <FormHelperText fontSize="xs">Our cost — never shown to the customer</FormHelperText>
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="xs" mb={1} opacity={0.75}>Sell price per node</FormLabel>
                <InputGroup size="sm">
                  <InputLeftAddon>$</InputLeftAddon>
                  <Input inputMode="decimal" value={price} placeholder="0.00" onChange={(e) => setPrice(e.target.value)} />
                </InputGroup>
              </FormControl>
            </SimpleGrid>
            <Box borderWidth="1px" borderRadius="md" p={3}>
              <Flex justify="space-between" fontSize="sm">
                <Text opacity={0.75}>{`${nodes} × ${valid ? money(unitPrice) : '—'}`}</Text>
                <Text fontWeight="bold">{valid ? money(unitPrice * nodes) : '—'}</Text>
              </Flex>
              <Flex justify="space-between" fontSize="xs" opacity={0.7} mt={1}>
                <Text>{`Cost ${valid ? money(unitCost * nodes) : '—'}`}</Text>
                <Text>{margin === null ? '' : `Margin ${margin.toFixed(1)}%`}</Text>
              </Flex>
            </Box>
            <Text fontSize="xs" opacity={0.7}>
              Creates ONE hardware line (quantity
              {' '}
              {nodes}
              ) with the full specification as its description, on a new sales order in this project. Tax starts off — set it on the sales order like any other.
            </Text>
          </Stack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button colorScheme="brand" onClick={onConvert} isLoading={convert.isLoading} isDisabled={!valid}>
            Create sales order
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ServerSpecConvertModal;
